/**
 * アクセスポリシー — ユーザーに割り当てる 1 つの束。
 *
 * 判定の順番(`server/app/idp/README.md` の R7d 節と同じ):
 *
 *   1. 割当が無ければ既定のポリシー。既定も無ければ**許可**(何も設定していない状態で
 *      ログインを止めない)
 *   2. 「入れる条件」が偽 → 拒否
 *   3. ログイン先のサービスが許可されていない(または条件が偽)→ 拒否
 *   4. 「追加認証を省ける条件」が真 → 許可、偽 → 追加認証
 *
 * `cookieTtlHours` と「自分でパスワードを再設定できる」は**保存するだけ**で、
 * まだ Keycloak には送っていない(R7e)。
 */
import {
  Alert,
  Button,
  Card,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useMemo, useState } from "react";

import type { PolicyGroup, PolicyGroupSp } from "../../lib/api";
import { jst } from "../../lib/format";
import { errorText, useIdpPerms } from "./hooks";
import { AtomHelp, ExprField } from "./ExprField";
import {
  useCreatePolicyGroup,
  useDeletePolicyGroup,
  usePolicyGroup,
  usePolicyGroups,
  usePolicySps,
  useUpdatePolicyGroup,
} from "./policyHooks";
import {
  ALLOW_DEFAULT,
  COOKIE_TTL_DEFAULT,
  COOKIE_TTL_MAX,
  COOKIE_TTL_MIN,
  OTP_SETUP_DEFAULT,
  OTP_SKIP_DEFAULT,
} from "./policyOptions";

const { Text, Paragraph } = Typography;

type Values = {
  displayName: string;
  isDefault: boolean;
  selfPasswordReset: boolean;
  allowExpr: string;
  otpSkipExpr: string;
  otpSetupExpr: string;
  cookieTtlHours: number;
};

type SpState = Record<number, { allowed: boolean; expr: string }>;

/** SP ごとの許可。既定は「許可・条件なし」で、行を触ったぶんだけ送る。 */
function SpTable({
  sps,
  state,
  onChange,
  disabled,
}: {
  sps: PolicyGroupSp[];
  state: SpState;
  onChange: (next: SpState) => void;
  disabled: boolean;
}) {
  const columns: ColumnsType<PolicyGroupSp> = [
    {
      title: "サービス",
      width: 240,
      dataIndex: "displayName",
      render: (v: string, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v || row.name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.name}
            {row.enabled ? "" : "(停止中)"}
          </Text>
        </Space>
      ),
    },
    {
      title: "使わせる",
      key: "allowed",
      width: 160,
      render: (_: unknown, row) => (
        <Switch
          size="small"
          disabled={disabled}
          checked={state[row.id]?.allowed ?? true}
          onChange={(v) =>
            onChange({
              ...state,
              [row.id]: { allowed: v, expr: state[row.id]?.expr ?? "" },
            })
          }
        />
      ),
    },
    {
      title: "条件(空欄なら無条件)",
      width: 400,
      ellipsis: true,
      onCell: (row) => ({ title: state[row.id]?.expr ?? "" }),
      key: "expr",
      render: (_: unknown, row) => (
        <Input
          size="small"
          disabled={disabled || !(state[row.id]?.allowed ?? true)}
          maxLength={512}
          placeholder="ip:本社"
          value={state[row.id]?.expr ?? ""}
          onChange={(e) =>
            onChange({
              ...state,
              [row.id]: {
                allowed: state[row.id]?.allowed ?? true,
                expr: e.target.value,
              },
            })
          }
        />
      ),
    },
  ];
  return (
    <Table<PolicyGroupSp>
      scroll={{ x: "max-content" }}
      rowKey="id"
      size="small"
      columns={columns}
      dataSource={sps}
      pagination={false}
      locale={{ emptyText: "サービスプロバイダーがまだ無い" }}
    />
  );
}

function GroupDrawer({
  id,
  creating,
  onClose,
}: {
  id: string | null;
  creating: boolean;
  onClose: () => void;
}) {
  const { canAdmin } = useIdpPerms();
  const detail = usePolicyGroup(id);
  const spList = usePolicySps(creating);
  const create = useCreatePolicyGroup();
  const update = useUpdatePolicyGroup();
  const [form] = Form.useForm<Values>();
  const [sps, setSps] = useState<SpState>({});
  const allow = Form.useWatch("allowExpr", form) ?? "";
  const otpSkip = Form.useWatch("otpSkipExpr", form) ?? "";
  const otpSetup = Form.useWatch("otpSetupExpr", form) ?? "";
  const open = creating || !!id;
  const d = detail.data;

  const rows = useMemo(
    () => (creating ? (spList.data?.items ?? []) : (d?.sps ?? [])),
    [creating, spList.data, d],
  );

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      displayName: creating ? "" : (d?.displayName ?? ""),
      isDefault: creating ? false : (d?.isDefault ?? false),
      selfPasswordReset: creating ? false : (d?.selfPasswordReset ?? false),
      allowExpr: creating ? ALLOW_DEFAULT : (d?.allowExpr ?? ALLOW_DEFAULT),
      otpSkipExpr: creating ? OTP_SKIP_DEFAULT : (d?.otpSkipExpr ?? OTP_SKIP_DEFAULT),
      otpSetupExpr: creating ? OTP_SETUP_DEFAULT : (d?.otpSetupExpr ?? OTP_SETUP_DEFAULT),
      cookieTtlHours: creating ? COOKIE_TTL_DEFAULT : (d?.cookieTtlHours ?? COOKIE_TTL_DEFAULT),
    });
    const next: SpState = {};
    for (const sp of creating ? (spList.data?.items ?? []) : (d?.sps ?? [])) {
      next[sp.id] = { allowed: sp.allowed ?? true, expr: sp.expr ?? "" };
    }
    setSps(next);
  }, [open, creating, d, spList.data, form]);

  const submit = (v: Values) => {
    const data = {
      displayName: v.displayName,
      isDefault: !!v.isDefault,
      selfPasswordReset: !!v.selfPasswordReset,
      allowExpr: v.allowExpr,
      otpSkipExpr: v.otpSkipExpr,
      otpSetupExpr: v.otpSetupExpr,
      cookieTtlHours: Number(v.cookieTtlHours ?? COOKIE_TTL_DEFAULT),
      // API が読むのは `spId`(一覧は `id` で返るが、書くときはこちら)
      sps: rows.map((sp) => ({
        spId: sp.id,
        allowed: sps[sp.id]?.allowed ?? true,
        expr: sps[sp.id]?.expr ?? "",
      })),
    };
    const done = { onSuccess: onClose };
    if (creating) create.mutate(data, done);
    else if (id) update.mutate({ id, data }, done);
  };

  return (
    <Drawer
      open={open}
      width={720}
      title={creating ? "ポリシーの追加" : (d?.displayName ?? "ポリシー")}
      onClose={onClose}
      destroyOnClose
      extra={
        canAdmin && (
          <Button
            type="primary"
            loading={create.isPending || update.isPending}
            onClick={() => void form.submit()}
          >
            {creating ? "追加する" : "保存する"}
          </Button>
        )
      }
    >
      {detail.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="ポリシーを取得できなかった"
          description={errorText(detail.error)}
        />
      )}
      <Form form={form} layout="vertical" onFinish={submit} disabled={!canAdmin}>
        <Form.Item
          name="displayName"
          label="名前"
          rules={[{ required: true, message: "名前は必須" }]}
        >
          <Input maxLength={100} placeholder="標準" />
        </Form.Item>
        <Flex gap={16} wrap>
          <Form.Item
            name="isDefault"
            label="既定のポリシー"
            valuePropName="checked"
            extra="割当が無い人に効く。既定は 1 つだけ(ここを入れると今の既定は外れる)。"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="selfPasswordReset"
            label="自分でパスワードを再設定できる"
            valuePropName="checked"
            extra="いまは保存するだけ(Keycloak には送っていない)。"
          >
            <Switch />
          </Form.Item>
        </Flex>

        <Card size="small" title={<Space>ログインの条件<AtomHelp /></Space>} style={{ marginBottom: 12 }}>
          <ExprField
            name="allowExpr"
            label="入れる条件"
            value={allow}
            extra="偽なら拒否。既定は always(常に通す)。"
          />
          <ExprField
            name="otpSkipExpr"
            label="追加認証を省ける条件"
            value={otpSkip}
            extra="真なら追加認証なしで通す。偽ならワンタイムパスワードを聞く。"
          />
          <ExprField
            name="otpSetupExpr"
            label="未登録の人に登録させる条件"
            value={otpSetup}
            extra="真のとき、ワンタイムパスワードが未登録の人にその場で登録させる。"
          />
          <Form.Item
            name="cookieTtlHours"
            label="ログインを保つ時間(時間)"
            extra="いまは保存するだけ(Keycloak のセッション設定には送っていない)。"
          >
            <InputNumber min={COOKIE_TTL_MIN} max={COOKIE_TTL_MAX} style={{ width: 160 }} />
          </Form.Item>
        </Card>

        <Card size="small" title="サービスごとの許可">
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            既定は全部許可。止めたいサービスだけ切り、条件を入れると
            <Text code>その条件が真のときだけ</Text>使える。
          </Paragraph>
          <SpTable sps={rows} state={sps} onChange={setSps} disabled={!canAdmin} />
        </Card>
      </Form>
    </Drawer>
  );
}

export function PolicyGroupsPage() {
  const { canAdmin } = useIdpPerms();
  const query = usePolicyGroups();
  const remove = useDeletePolicyGroup();
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const columns: ColumnsType<PolicyGroup> = [
    {
      title: "名前",
      width: 240,
      dataIndex: "displayName",
      render: (v: string, row) => (
        <Space>
          <a onClick={() => setOpenId(row.id)}>{v}</a>
          {row.isDefault && <Tag color="blue">既定</Tag>}
        </Space>
      ),
    },
    {
      title: "入れる条件",
      width: 320,
      ellipsis: true,
      onCell: (row) => ({ title: String(row.allowExpr ?? "") }),
      dataIndex: "allowExpr",
      render: (v: string) => <Text code>{v}</Text>,
    },
    {
      title: "追加認証を省ける条件",
      width: 320,
      ellipsis: true,
      onCell: (row) => ({ title: String(row.otpSkipExpr ?? "") }),
      dataIndex: "otpSkipExpr",
      render: (v: string) => <Text code>{v}</Text>,
    },
    { title: "割当", dataIndex: "assignedCount", width: 160 },
    {
      title: "個別に設定した SP",
      dataIndex: "spCount",
      width: 172,
      render: (n: number) => (n > 0 ? `${n} 件` : <Text type="secondary">—</Text>),
    },
    {
      title: "更新",
      dataIndex: "updatedAt",
      width: 180,
      render: (v: string | null) => jst(v) || "—",
    },
    {
      title: "",
      key: "op",
      width: 90,
      render: (_: unknown, row) =>
        canAdmin && (
          <Button
            size="small"
            danger
            onClick={() =>
              Modal.confirm({
                title: `${row.displayName} を削除する`,
                content: row.assignedCount
                  ? `${row.assignedCount} 人に割り当てられているので、先に割当を外す。`
                  : "このポリシーの設定は消える(割り当てられている人は居ない)。",
                okText: "削除する",
                okButtonProps: { danger: true, disabled: row.assignedCount > 0 },
                cancelText: "やめる",
                onOk: () => remove.mutate(row.id),
              })
            }
          >
            削除
          </Button>
        ),
    },
  ];

  return (
    <div>
      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          アクセスポリシー
        </Typography.Title>
        {canAdmin && (
          <Button type="primary" onClick={() => setCreating(true)}>
            ポリシーの追加
          </Button>
        )}
      </Flex>

      <Paragraph type="secondary">
        ログインのときに効く条件の束。ユーザー一覧から 1 人ずつ割り当てる。
        割当が無い人には<Text strong>既定</Text>のポリシーが効く。
        実際にログインを止めるかどうかは「ポリシー設定」の有効/無効で決まる。
      </Paragraph>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="ポリシーを取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Card size="small">
        <Table<PolicyGroup>
          scroll={{ x: "max-content" }}
          rowKey="id"
          size="small"
          loading={query.isFetching || remove.isPending}
          columns={columns}
          dataSource={query.data?.items ?? []}
          pagination={false}
          locale={{ emptyText: "ポリシーはまだ無い" }}
        />
      </Card>

      <GroupDrawer
        id={creating ? null : openId}
        creating={creating}
        onClose={() => {
          setCreating(false);
          setOpenId(null);
        }}
      />
    </div>
  );
}
